import React from 'react';
import PropTypes from 'prop-types';
import PlaceholderDiagram from './PlaceholderDiagram';
import PageCard from './PageCard';
import SheetPair from './SheetPair';
import { getAdjustedPageNumber } from '../utils/pageCalculations';
import usePdfThumbnails from '../utils/usePdfThumbnails';
import computePreviewTransforms from '../utils/previewTransforms';
import { MAX_PAGES_PER_ROW } from '../constants';

/**
 * SVG component for displaying ellipsis between page groups
 * Indicates hidden pages/signatures in the diagram
 * 
 * @component
 * @private
 */
const EllipsisIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className="ellipsis-icon"
    aria-label="Hidden pages indicator"
  >
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

/**
 * PageLayoutDiagram Component
 * 
 * Provides a visual representation of how pages will be arranged in the final booklet.
 * Shows both the input PDF layout and the output signature arrangement.
 * Handles large documents by showing first/last pages with ellipsis for hidden pages.
 *
 * @component
 */
const PageLayoutDiagram = ({ 
  sourcePages, 
  signatures, 
  pageOrientation = 'portrait',
  showHoleGuides = false,
  showFoldLine = false,
  startPadding = 0,
  endPadding = 0,
  showPageNumbers = false,
  showPageImages = true,
  pdfFile = null,
  pdfScale = 1,
  pdfTranslateX = 0,
  pdfTranslateY = 0,
  scaleFromSheetCenter = false,
  paperSize = null,
  showOutputPageNumbers = false,
  pageNumberOffset = 0,
  pageNumberFontSize = 12
}) => {
  const { getThumbnailForPage, isLoading: thumbnailsLoading } = usePdfThumbnails(pdfFile, { enabled: showPageImages });

  const previewTransforms = paperSize
    ? computePreviewTransforms({
        paperWidth: paperSize.width,
        paperHeight: paperSize.height,
        pdfScale,
        pdfTranslateX,
        pdfTranslateY,
        scaleFromSheetCenter,
      })
    : null;

  // Early return for loading/empty state
  if (!sourcePages || !signatures) {
    return <PlaceholderDiagram isLoading={pdfFile !== null} />;
  }

  // Generate complete page array including padding pages
  const allPages = [
    ...Array(startPadding).fill(''),
    ...Array.from({ length: sourcePages }, (_, i) => i + 1),
    ...Array(endPadding).fill('')
  ];

  // Configuration for page display
  const pagesPerRow = MAX_PAGES_PER_ROW;

  /**
   * Calculate which pages to display with ellipsis for long documents
   * Returns an array of page rows to display
   * @private
   */
  const calculateDisplayPages = () => {
    if (allPages.length <= pagesPerRow) {
      return {
        pages: [allPages],
        hasHidden: false
      };
    }

    const firstRow = allPages.slice(0, pagesPerRow);
    const remainingPages = allPages.slice(pagesPerRow);
    const lastRow = remainingPages.slice(-Math.min(remainingPages.length, pagesPerRow));
    
    return {
      pages: [firstRow, lastRow],
      hasHidden: remainingPages.length > lastRow.length
    };
  };

  /**
   * Calculate which signatures to display with ellipsis
   * Returns filtered signature array for display
   * @private
   */
  const calculateDisplaySignatures = () => {
    if (signatures.length <= 2) {
      return {
        sigs: signatures,
        hasHidden: false
      };
    }

    return {
      sigs: [signatures[0], signatures[signatures.length - 1]],
      hasHidden: true
    };
  };

  const { pages: displayPages, hasHidden: hasHiddenPages } = calculateDisplayPages();
  const { sigs: displaySignatures, hasHidden: hasHiddenSignatures } = calculateDisplaySignatures();

  return (
    <div className="page-layout-diagram">
      {/* Input PDF Layout Section */}
      <div className="diagram-section">
        <h2>Input PDF</h2>
        {displayPages.map((row, rowIndex) => (
          <React.Fragment key={`row-container-${rowIndex}`}>
            <div className="page-row">
              {row.map((num, idx) => {
                const pageNumber = typeof num === 'number' ? num : null;
                const thumbnailUrl = showPageImages && pageNumber ? getThumbnailForPage(pageNumber) : null;
                return (
                  <PageCard
                    key={`source-${rowIndex}-${idx}`}
                    pageNumbers={[num]}
                    isBlank={num === ''}
                    orientation={pageOrientation}
                    showDebugNumbers={!showPageImages || showPageNumbers}
                    showGuides={false}
                    showHoleGuides={false}
                    showFoldLine={false}
                    thumbnails={thumbnailUrl && showPageImages ? [thumbnailUrl] : []}
                    showPageImages={showPageImages}
                  />
                );
              })}
            </div>
            {rowIndex === 0 && displayPages.length > 1 && hasHiddenPages && (
              <div className="hidden-pages-indicator">
                {showPageImages && thumbnailsLoading ? (
                  <span className="thumbnail-loading-message">Loading Thumbnails...</span>
                ) : (
                  <EllipsisIcon />
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Output Signature Layout Section */}
      <div className="diagram-section">
        <h2>Output PDF</h2>
        {displaySignatures.map((signature, sigIndex) => {
          return (
          <React.Fragment key={`sig-container-${sigIndex}`}>
            <div className="page-row">
              {signature.map((sheet, sheetIndex) => {
                const [[frontRight, frontLeft], [backLeft, backRight]] = sheet;
                const frontPages = [
                  getAdjustedPageNumber(frontRight, startPadding, sourcePages),
                  getAdjustedPageNumber(frontLeft, startPadding, sourcePages)
                ];
                const backPages = [
                  getAdjustedPageNumber(backLeft, startPadding, sourcePages),
                  getAdjustedPageNumber(backRight, startPadding, sourcePages)
                ];
                
                return (
                  <SheetPair
                    key={`sig-${sigIndex}-sheet-${sheetIndex}`}
                    frontPages={frontPages}
                    backPages={backPages}
                    frontThumbnails={showPageImages ? frontPages.map((page) => getThumbnailForPage(page)) : []}
                    backThumbnails={showPageImages ? backPages.map((page) => getThumbnailForPage(page)) : []}
                    showPageNumbers={showPageNumbers || !showPageImages}
                    showHoleGuides={showHoleGuides}
                    showFoldLine={showFoldLine}
                    pageTransforms={previewTransforms}
                    showPageImages={showPageImages}
                    showOutputPageNumbers={showOutputPageNumbers}
                    pageNumberOffset={pageNumberOffset}
                    pageNumberFontSize={pageNumberFontSize}
                  />
                );
              })}
            </div>
            {sigIndex === 0 && hasHiddenSignatures && (
              <div className="hidden-pages-indicator">
                <EllipsisIcon />
              </div>
            )}
          </React.Fragment>
        );
        })}
      </div>
    </div>
  );
};

PageLayoutDiagram.propTypes = {
  sourcePages: PropTypes.number,      // Total number of pages in source PDF
  signatures: PropTypes.arrayOf(      // Array of signature arrangements
    PropTypes.arrayOf(                // Each signature contains sheets
      PropTypes.arrayOf(              // Each sheet has front/back arrays
        PropTypes.arrayOf(            // Each side has left/right page numbers
          PropTypes.number
        )
      )
    )
  ),
  pageOrientation: PropTypes.oneOf(['portrait', 'landscape']),
  showHoleGuides: PropTypes.bool,     // Show binding hole guides
  showFoldLine: PropTypes.bool,       // Show fold line guides
  startPadding: PropTypes.number,     // Number of blank pages at start
  endPadding: PropTypes.number,       // Number of blank pages at end
  showPageNumbers: PropTypes.bool,    // Show debug page numbers
  showPageImages: PropTypes.bool,
  pdfFile: PropTypes.object,          // Uploaded PDF file object
  pdfScale: PropTypes.number,
  pdfTranslateX: PropTypes.number,
  pdfTranslateY: PropTypes.number,
  scaleFromSheetCenter: PropTypes.bool,
  paperSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    name: PropTypes.string,
  })
};

export default PageLayoutDiagram; 
